import { checkNullString, getLastDateRange, getStaticDateText } from '@/pages/datalab/utils/utils';
import type { IColumnInfo } from '@dbl/core/dataSource';
import type { TDispatchFun } from '@/pages/datalab/components/SortableDrag/configration';
import { SortableType } from '@dbl/core/chartEditor';
import { ChartType, type TChartType } from '@dbl/core/chart';
import {
  FieldTypeDefaultAccuracy,
  getDefaultNumberUnit,
} from '@dbl/core/chartEditor';

/**
 * 分类维度 / X 轴区写回 model：拖拽、close、批量下拉。
 * 列排序由 useQBLinkagePipeline 维护；Trend 类联动 columnFilters。
 */

export interface IDimensionSortablePersistOptions {
  sortableKey: string;
  chartType: string;
  /** Trend 删维度 / 补默认日期筛选用，对应当前 columnFilters */
  columnFilters?: IColumnInfo[];
  dispatchFun: TDispatchFun;
  saveColumnInfos?: Record<string, unknown>;
}

export interface IDimensionSortablePersist {
  saveList: (list: IColumnInfo[]) => boolean;
  removeItems: (currentList: IColumnInfo[], removedItems: IColumnInfo[]) => void;
  /**
   * 批量下拉关闭写回。
   * removedItems：取消勾选时传入，Trend 图用于同步剔除 columnFilters。
   */
  applyBatchChange: (
    nextDimensions: IColumnInfo[],
    removedItems?: IColumnInfo[],
  ) => void;
}

export const normalizeDimensionField = (
  item: IColumnInfo,
  chartType: string,
): IColumnInfo => ({
  ...item,
  accuracy: item.accuracy ?? FieldTypeDefaultAccuracy[item.column_type],
  unit: (item.unit ??
    getDefaultNumberUnit({ chartType, fieldType: item.column_type })) as IColumnInfo['unit'],
});

export const createDimensionSortablePersist = (
  options: IDimensionSortablePersistOptions,
): IDimensionSortablePersist => {
  const { sortableKey, chartType, columnFilters, dispatchFun, saveColumnInfos } =
    options;

  /** 分类维度区与 X 轴区拖入日期首维时，均需补默认 columnFilters */
  const shouldSyncTrendDateFilter =
    sortableKey === SortableType.Dimension ||
    sortableKey === SortableType.XColumn;

  const normalizeList = (list: IColumnInfo[]): IColumnInfo[] =>
    list
      .filter(item => !checkNullString(item.id))
      .map(item => normalizeDimensionField(item, chartType));

  const buildSavePayload = (
    nextDimensions: IColumnInfo[],
    removedItems: IColumnInfo[] = [],
  ): Record<string, unknown> => {
    const savePayload: Record<string, unknown> = {
      [sortableKey]: nextDimensions,
    };
    if (saveColumnInfos) {
      savePayload.saveColumnInfos = saveColumnInfos;
    }
    if ([ChartType.Trend, ChartType.DimNumberTrend].includes(chartType as TChartType) && removedItems.length > 0) {
      const removedIds = new Set(removedItems.map(item => item.id));
      savePayload.columnFilters = (columnFilters || []).filter(
        item => !checkNullString(item.id) && !removedIds.has(item.id),
      );
    }
    if (
      shouldSyncTrendDateFilter &&
      [ChartType.Trend, ChartType.DimNumberTrend].includes(chartType as TChartType) &&
      nextDimensions.length > 0
    ) {
      const dateInfo = nextDimensions[0];
      const filterList = (columnFilters || []).filter(
        item => !checkNullString(item.id),
      );
      if (
        dateInfo.column_type === 'date' &&
        !filterList.some(item => item.id === dateInfo.id)
      ) {
        const dateTypeVal = dateInfo.column_granularity?.replace(/\d/, '') || 'day';
        const text = getStaticDateText(getLastDateRange(1, dateTypeVal), dateTypeVal);
        savePayload.columnFilters = [
          ...filterList,
          {
            ...dateInfo,
            column_text: text,
            column_value: [`1-${dateTypeVal}`],
            dateType: dateTypeVal,
            lastCount: 1,
            operation: 'recent',
          },
        ];
      }
    }
    return savePayload;
  };

  const saveList = (list: IColumnInfo[]): boolean => {
    const nextDimensions = normalizeList(list);

    if (
      [ChartType.Trend, ChartType.DimNumberTrend].includes(chartType as TChartType) &&
      nextDimensions.length > 0 &&
      nextDimensions[0].column_type !== 'date'
    ) {
      dispatchFun('saveDrag', {
        dragState: {
          currentDrag: sortableKey,
          errorDrag: { [sortableKey]: `${ChartType.Trend}_dimension` },
        },
      });
      return false;
    }

    dispatchFun('save', buildSavePayload(nextDimensions));
    return true;
  };

  const removeItems = (
    currentList: IColumnInfo[],
    removedItems: IColumnInfo[],
  ): void => {
    if (!removedItems.length) {
      return;
    }
    const removedKeySet = new Set(
      removedItems.map(item => `${item.id}_${item.table_code}`),
    );
    const nextDimensions = currentList.filter(
      item =>
        !checkNullString(item.id) &&
        !removedKeySet.has(`${item.id}_${item.table_code}`),
    );
    dispatchFun('save', buildSavePayload(nextDimensions, removedItems));
  };

  const applyBatchChange = (
    nextDimensions: IColumnInfo[],
    removedItems: IColumnInfo[] = [],
  ): void => {
    dispatchFun(
      'save',
      buildSavePayload(normalizeList(nextDimensions), removedItems),
    );
  };

  return {
    saveList,
    removeItems,
    applyBatchChange,
  };
};

